/**
 * Provider COST of the LLMs this platform calls, in USD, and the conversions
 * from token usage to dollars.
 *
 * This is the single provider-cost source (PD-16759). It answers "what did
 * this call cost Workd", which feeds `llm_cost` on agent messages, spend
 * budgets in the background pipelines and, later, `costUsd` on usage events.
 * It has nothing to do with what a tenant is charged: customer credits come
 * from the versioned `ai_rate_card` that Workd admins manage in config-web.
 *
 * Why code constants and not a table: provider prices change rarely and are
 * engineering-owned, and a git history of each rate is the audit trail. Raw
 * token counts are always stored next to any computed cost, so a cost is
 * recomputable if a rate here was wrong for a period.
 *
 * Rates are provider LIST prices in USD per MILLION tokens. Anthropic rates
 * were verified against the Anthropic model catalog (2026-08-20; Opus 4.8 on
 * 2026-10-05; Sonnet 5.5 and Opus 5.5 on 2026-10-10); OpenAI rates against
 * OpenAI's public pricing (2026-07-03; gpt-5.4 on 2026-10-05; gpt-6-luna on
 * 2026-10-11). Where an introductory discount exists we keep the standard
 * sticker rate: estimates and budgets should be conservative, and a
 * budget tuned against a temporary discount would silently loosen when it
 * ends.
 *
 * Cache columns:
 * - `cacheReadUsdPerMTok` is the discounted rate for prompt tokens served from
 *   the provider's prompt cache (Anthropic cache reads, OpenAI cached input).
 * - `cacheWriteUsdPerMTok` is Anthropic's cache-creation rate (1.25x input,
 *   5-minute TTL, which is the only TTL the platform requests). OpenAI has no
 *   write premium, so its value equals input.
 *
 * Usage and cost (PD-16963): every provider's usage is converted into one
 * shape, `LlmUsageForPricingModel`, by the normalizers below, and priced by
 * one function, `usageToUsd`. Agent turns, the background pipelines and their
 * budgets all go through it, so the same tokens cost the same dollars
 * wherever they are reported.
 */

export type LlmProviderName = 'anthropic' | 'openai';

/** One rate card: USD per 1,000,000 tokens of each billed kind. */
export interface LlmTokenRatesModel {
	/** USD per 1,000,000 fresh (uncached) input tokens. */
	inputUsdPerMTok: number;
	/** USD per 1,000,000 output tokens. */
	outputUsdPerMTok: number;
	/** USD per 1,000,000 cache-read input tokens. */
	cacheReadUsdPerMTok: number;
	/** USD per 1,000,000 cache-write input tokens. */
	cacheWriteUsdPerMTok: number;
}

/**
 * A second rate card a model bills for a request whose prompt is long
 * (PD-16963). It replaces the standard card for the WHOLE request, output and
 * cache tokens included, not only for the tokens past the threshold.
 */
export interface LlmLongPromptRatesModel extends LlmTokenRatesModel {
	/**
	 * The card applies when the request's whole prompt (uncached input, cache
	 * reads and cache writes together) is MORE than this many tokens.
	 */
	promptTokensAbove: number;
}

export interface LlmModelCostModel extends LlmTokenRatesModel {
	/** Model id as sent on the API request (or its alias for dated snapshots). */
	modelId: string;
	provider: LlmProviderName;
	/** Absent when the model bills every prompt length at the standard card. */
	longPrompt?: LlmLongPromptRatesModel;
}

/**
 * Every model with a known provider cost. A model the platform supports must
 * have an entry here; `llm-pricing.test.ts` fails otherwise.
 *
 * Retired models (OpenAI gpt-4.1 and gpt-4o; Claude 3 and Claude 4.0) stay
 * listed: messages recorded before the cutover carry those names, and their
 * cost must stay recomputable.
 */
export const LLM_MODEL_COSTS: Array<LlmModelCostModel> = [
	// --- Anthropic: current ---
	{
		// PD-16923 — runs Settings › Languages "Fill missing with AI", and is an
		// agent model since PD-16962. Anthropic list price, checked 2026-10-08
		// and again 2026-10-10 (PD-16963), for prompts up to 100,000 tokens:
		// $0.10 / $0.50 per MTok, cache read $0.01 (0.1x input), 5-minute cache
		// write $0.125 (1.25x input).
		modelId: 'claude-haiku-5-5',
		provider: 'anthropic',
		inputUsdPerMTok: 0.1,
		outputUsdPerMTok: 0.5,
		cacheReadUsdPerMTok: 0.01,
		cacheWriteUsdPerMTok: 0.125,
		// PD-16963 — a prompt over 100,000 tokens bills the whole request on a
		// second card: $0.50 / $2.50 per MTok, with cache reads and writes at
		// the same 0.1x and 1.25x of that input rate. The threshold counts the
		// whole prompt, cache reads and writes included, the way Anthropic
		// counts its other long-context thresholds.
		longPrompt: {
			promptTokensAbove: 100_000,
			inputUsdPerMTok: 0.5,
			outputUsdPerMTok: 2.5,
			cacheReadUsdPerMTok: 0.05,
			cacheWriteUsdPerMTok: 0.625,
		},
	},
	{
		modelId: 'claude-haiku-4-5',
		provider: 'anthropic',
		inputUsdPerMTok: 1,
		outputUsdPerMTok: 5,
		cacheReadUsdPerMTok: 0.1,
		cacheWriteUsdPerMTok: 1.25,
	},
	{
		// PD-16962 — Anthropic list price from the Claude API model catalog
		// (checked 2026-10-10): $2 / $10 per MTok, cache read $0.20, 5-minute
		// cache write $2.50. Same rates as Claude Sonnet 5.
		modelId: 'claude-sonnet-5-5',
		provider: 'anthropic',
		inputUsdPerMTok: 2,
		outputUsdPerMTok: 10,
		cacheReadUsdPerMTok: 0.2,
		cacheWriteUsdPerMTok: 2.5,
	},
	{
		// PD-16962 — Anthropic list price from the Claude API model catalog
		// (checked 2026-10-10): $4 / $20 per MTok, 5-minute cache write $5.
		// Cache reads are $0.20, which is 0.05x input on this model rather than
		// the usual 0.1x.
		modelId: 'claude-opus-5-5',
		provider: 'anthropic',
		inputUsdPerMTok: 4,
		outputUsdPerMTok: 20,
		cacheReadUsdPerMTok: 0.2,
		cacheWriteUsdPerMTok: 5,
	},
	{
		modelId: 'claude-sonnet-5',
		provider: 'anthropic',
		// Anthropic list price, checked 2026-10-05: $2 / $10 per MTok, cache
		// read 0.1x and 5-minute cache write 1.25x of input.
		inputUsdPerMTok: 2,
		outputUsdPerMTok: 10,
		cacheReadUsdPerMTok: 0.2,
		cacheWriteUsdPerMTok: 2.5,
	},
	{
		modelId: 'claude-opus-5',
		provider: 'anthropic',
		inputUsdPerMTok: 5,
		outputUsdPerMTok: 25,
		cacheReadUsdPerMTok: 0.5,
		cacheWriteUsdPerMTok: 6.25,
	},
	{
		modelId: 'claude-opus-4-8',
		provider: 'anthropic',
		inputUsdPerMTok: 5,
		outputUsdPerMTok: 25,
		cacheReadUsdPerMTok: 0.5,
		cacheWriteUsdPerMTok: 6.25,
	},
	// --- Anthropic: retired (kept so historical cost stays recomputable) ---
	// PD-16962: none of these is served by the API any more, so none is in
	// AGENT_LLM_MODELS; messages recorded while they ran carry their names.
	{
		modelId: 'claude-sonnet-4-20250514',
		provider: 'anthropic',
		inputUsdPerMTok: 3,
		outputUsdPerMTok: 15,
		cacheReadUsdPerMTok: 0.3,
		cacheWriteUsdPerMTok: 3.75,
	},
	{
		modelId: 'claude-opus-4-20250514',
		provider: 'anthropic',
		inputUsdPerMTok: 15,
		outputUsdPerMTok: 75,
		cacheReadUsdPerMTok: 1.5,
		cacheWriteUsdPerMTok: 18.75,
	},
	{
		modelId: 'claude-3-5-sonnet-20241022',
		provider: 'anthropic',
		inputUsdPerMTok: 3,
		outputUsdPerMTok: 15,
		cacheReadUsdPerMTok: 0.3,
		cacheWriteUsdPerMTok: 3.75,
	},
	{
		modelId: 'claude-3-5-haiku-20241022',
		provider: 'anthropic',
		inputUsdPerMTok: 0.8,
		outputUsdPerMTok: 4,
		cacheReadUsdPerMTok: 0.08,
		cacheWriteUsdPerMTok: 1,
	},
	{
		modelId: 'claude-3-opus-20240229',
		provider: 'anthropic',
		inputUsdPerMTok: 15,
		outputUsdPerMTok: 75,
		cacheReadUsdPerMTok: 1.5,
		cacheWriteUsdPerMTok: 18.75,
	},
	{
		modelId: 'claude-3-sonnet-20240229',
		provider: 'anthropic',
		inputUsdPerMTok: 3,
		outputUsdPerMTok: 15,
		cacheReadUsdPerMTok: 0.3,
		cacheWriteUsdPerMTok: 3.75,
	},
	{
		modelId: 'claude-3-haiku-20240307',
		provider: 'anthropic',
		inputUsdPerMTok: 0.25,
		outputUsdPerMTok: 1.25,
		cacheReadUsdPerMTok: 0.03,
		cacheWriteUsdPerMTok: 0.3,
	},
	// --- OpenAI: current ---
	{
		modelId: 'gpt-5.4',
		provider: 'openai',
		inputUsdPerMTok: 2.5,
		outputUsdPerMTok: 15,
		cacheReadUsdPerMTok: 0.25,
		cacheWriteUsdPerMTok: 2.5,
	},
	{
		modelId: 'gpt-5.4-mini',
		provider: 'openai',
		inputUsdPerMTok: 0.75,
		outputUsdPerMTok: 4.5,
		cacheReadUsdPerMTok: 0.075,
		cacheWriteUsdPerMTok: 0.75,
	},
	{
		modelId: 'gpt-5.4-nano',
		provider: 'openai',
		inputUsdPerMTok: 0.2,
		outputUsdPerMTok: 1.25,
		cacheReadUsdPerMTok: 0.02,
		cacheWriteUsdPerMTok: 0.2,
	},
	{
		modelId: 'gpt-6-luna',
		provider: 'openai',
		inputUsdPerMTok: 0.1,
		outputUsdPerMTok: 0.5,
		cacheReadUsdPerMTok: 0.01,
		cacheWriteUsdPerMTok: 0.1,
	},
	// --- OpenAI: retired (kept so historical cost stays recomputable) ---
	{
		modelId: 'gpt-4.1',
		provider: 'openai',
		inputUsdPerMTok: 2,
		outputUsdPerMTok: 8,
		cacheReadUsdPerMTok: 0.5,
		cacheWriteUsdPerMTok: 2,
	},
	{
		modelId: 'gpt-4.1-mini',
		provider: 'openai',
		inputUsdPerMTok: 0.4,
		outputUsdPerMTok: 1.6,
		cacheReadUsdPerMTok: 0.1,
		cacheWriteUsdPerMTok: 0.4,
	},
	{
		modelId: 'gpt-4.1-nano',
		provider: 'openai',
		inputUsdPerMTok: 0.1,
		outputUsdPerMTok: 0.4,
		cacheReadUsdPerMTok: 0.025,
		cacheWriteUsdPerMTok: 0.1,
	},
	{
		modelId: 'gpt-4o',
		provider: 'openai',
		inputUsdPerMTok: 2.5,
		outputUsdPerMTok: 10,
		cacheReadUsdPerMTok: 1.25,
		cacheWriteUsdPerMTok: 2.5,
	},
	{
		modelId: 'gpt-4o-mini',
		provider: 'openai',
		inputUsdPerMTok: 0.15,
		outputUsdPerMTok: 0.6,
		cacheReadUsdPerMTok: 0.075,
		cacheWriteUsdPerMTok: 0.15,
	},
];

/**
 * A provider snapshot suffix: `-20251001` (Anthropic) or `-2025-04-14`
 * (OpenAI).
 */
const SNAPSHOT_SUFFIX = /^-(\d{8}|\d{4}-\d{2}-\d{2})$/;

/**
 * Resolve a model name to one of `keys`: an exact match, or a key the name
 * extends with only a dated snapshot suffix. A dated snapshot
 * (`claude-haiku-4-5-20251001`, `gpt-4.1-mini-2025-04-14`) bills at its
 * alias's rate, but a different model that merely shares a prefix
 * (`gpt-5.4-mini` against `gpt-5.4`, `claude-sonnet-50`) never resolves, so
 * it is reported as unpriced rather than charged at another model's rate.
 *
 * Shared by the cost table here and the credit rate card, so a model name
 * resolves to the same entry in both.
 */
export const resolveModelKey = (
	modelId: string | null | undefined,
	keys: Iterable<string>,
): string | null => {
	if (modelId == null || modelId === '') {
		return null;
	}

	let snapshotOf: string | null = null;

	for (const key of keys) {
		if (key === modelId) {
			return key;
		}

		if (
			modelId.startsWith(key) &&
			SNAPSHOT_SUFFIX.test(modelId.slice(key.length))
		) {
			snapshotOf = key;
		}
	}

	return snapshotOf;
};

/** The provider cost entry for a model, or null when it has none. */
export const findLlmModelCost = (
	modelId: string | null | undefined,
): LlmModelCostModel | null => {
	const key = resolveModelKey(
		modelId,
		LLM_MODEL_COSTS.map((entry) => entry.modelId),
	);

	if (key == null) {
		return null;
	}

	return LLM_MODEL_COSTS.find((entry) => entry.modelId === key) ?? null;
};

/**
 * The hosted web_search surcharge: $10 per 1,000 searches, billed per
 * request regardless of model.
 */
export const WEB_SEARCH_USD_PER_REQUEST = 0.01;

/**
 * A model the background pipelines (email ingest, external lookup, product
 * matching) may be configured to run, with its picker label.
 */
export interface LlmModelPricingModel extends LlmModelCostModel {
	/** Human label for step-config model pickers. */
	label: string;
}

const pipelineModel = (modelId: string, label: string): LlmModelPricingModel => {
	const cost = LLM_MODEL_COSTS.find((entry) => entry.modelId === modelId);

	// Fails at module load, so a typo here breaks every test run immediately.
	if (cost == null) {
		throw new Error(`No provider cost entry for pipeline model ${modelId}`);
	}

	return { ...cost, label };
};

/**
 * Models the background pipelines may run. The rates come from
 * `LLM_MODEL_COSTS`, so there is one number per model. Order matters: step
 * config pickers render this order, so keep it cheapest-first.
 */
export const LLM_MODEL_PRICING: Array<LlmModelPricingModel> = [
	pipelineModel('claude-haiku-4-5', 'Claude Haiku 4.5 (fastest, lowest cost)'),
	pipelineModel('claude-sonnet-5', 'Claude Sonnet 5 (balanced)'),
	pipelineModel('claude-opus-5', 'Claude Opus 5 (highest capability)'),
];

/**
 * The pipeline-picker entry for a model, or null when the model is not one a
 * pipeline may be configured to run. Use `findLlmModelCost` to price any
 * model; use this to validate a pipeline's configured model.
 */
export const findLlmModelPricing = (
	modelId: string | null | undefined,
): LlmModelPricingModel | null => {
	const key = resolveModelKey(
		modelId,
		LLM_MODEL_PRICING.map((entry) => entry.modelId),
	);

	if (key == null) {
		return null;
	}

	return LLM_MODEL_PRICING.find((entry) => entry.modelId === key) ?? null;
};

/**
 * One LLM call's usage, the same shape for every provider (PD-16963). Every
 * cost in this file is computed from it.
 *
 * The four token counts never overlap; each is billed at its own rate:
 * - `inputTokens`: prompt tokens neither read from nor written to the prompt
 *   cache, at the plain input rate.
 * - `cacheReadInputTokens`: prompt tokens served from the prompt cache, at the
 *   discounted cache-read rate.
 * - `cacheCreationInputTokens`: prompt tokens written to the prompt cache, at
 *   the cache-write rate (a premium on Anthropic; OpenAI bills none).
 * - `outputTokens`: completion tokens, reasoning included.
 *
 * The whole prompt is the sum of the first three (`llmPromptTokens`).
 *
 * The providers report usage in different shapes: OpenAI's `prompt_tokens`
 * INCLUDES its cached tokens, Anthropic's `input_tokens` EXCLUDES cache reads
 * and cache writes. Reading one provider's fields with the other's meaning
 * double-counts or drops cache tokens, so build this shape with the
 * normalizer for the source rather than by mapping fields by hand:
 * `normalizeOpenAiUsage`, `normalizeAnthropicUsage`, or
 * `normalizePromptInclusiveUsage` for the agent usage events and the
 * `_conversation_message` columns.
 */
export interface LlmUsageForPricingModel {
	inputTokens: number;
	outputTokens: number;
	cacheReadInputTokens?: number | null;
	cacheCreationInputTokens?: number | null;
	webSearchRequests?: number | null;
}

/** Usage as OpenAI Chat Completions reports it. `prompt_tokens` INCLUDES the cached tokens. */
export interface OpenAiRawUsageModel {
	prompt_tokens?: number | null;
	completion_tokens?: number | null;
	prompt_tokens_details?: { cached_tokens?: number | null } | null;
}

/**
 * Usage as the Anthropic Messages API reports it. `input_tokens` EXCLUDES
 * cache reads and cache writes, which are reported separately.
 */
export interface AnthropicRawUsageModel {
	input_tokens?: number | null;
	output_tokens?: number | null;
	cache_read_input_tokens?: number | null;
	cache_creation_input_tokens?: number | null;
	server_tool_use?: { web_search_requests?: number | null } | null;
}

/**
 * Usage whose prompt count is the WHOLE prompt, with the cached parts as
 * subsets of it: the agent providers' usage events and the
 * `_conversation_message` columns (`prompt_tokens`, `cached_tokens`,
 * `cache_creation_tokens`).
 */
export interface LlmPromptInclusiveUsageModel {
	promptTokens?: number | null;
	completionTokens?: number | null;
	cachedTokens?: number | null;
	cacheCreationTokens?: number | null;
}

/**
 * A reported count as a usable number. Absent, negative and non-numeric
 * values read as zero: a NaN would poison every total it is added to, and no
 * provider reports a negative count.
 */
const tokenCount = (value: number | null | undefined): number =>
	value != null && Number.isFinite(value) && value > 0 ? value : 0;

/** The whole prompt of a call: uncached input, cache reads and cache writes. */
export const llmPromptTokens = (usage: LlmUsageForPricingModel): number =>
	tokenCount(usage.inputTokens) +
	tokenCount(usage.cacheReadInputTokens) +
	tokenCount(usage.cacheCreationInputTokens);

/**
 * OpenAI usage in the normalized shape. The cached tokens are taken OUT of
 * the prompt count, and clamped to it (providers never report more cached
 * than prompt tokens; a NaN-free total matters more than trusting one that
 * did). Cache writes are zero, not unknown: OpenAI caches prompts without a
 * write charge, so none of its prompt is billed at a write rate. Cache reads
 * stay null when the response carried no `prompt_tokens_details`, so a missing
 * report is never shown as "nothing was cached".
 */
export const normalizeOpenAiUsage = (
	raw: OpenAiRawUsageModel | null | undefined,
): LlmUsageForPricingModel | null => {
	if (raw == null) {
		return null;
	}

	const promptTokens = tokenCount(raw.prompt_tokens);
	const reportedCached = raw.prompt_tokens_details?.cached_tokens;
	const cachedTokens = Math.min(tokenCount(reportedCached), promptTokens);

	return {
		inputTokens: promptTokens - cachedTokens,
		outputTokens: tokenCount(raw.completion_tokens),
		cacheReadInputTokens: reportedCached != null ? cachedTokens : null,
		cacheCreationInputTokens: 0,
	};
};

/**
 * Anthropic usage in the normalized shape. Its counts are already disjoint,
 * so they map one to one; a null cache count means the request used no
 * cache, which is zero.
 */
export const normalizeAnthropicUsage = (
	raw: AnthropicRawUsageModel | null | undefined,
): LlmUsageForPricingModel | null => {
	if (raw == null) {
		return null;
	}

	const webSearchRequests = raw.server_tool_use?.web_search_requests;

	return {
		inputTokens: tokenCount(raw.input_tokens),
		outputTokens: tokenCount(raw.output_tokens),
		cacheReadInputTokens: tokenCount(raw.cache_read_input_tokens),
		cacheCreationInputTokens: tokenCount(raw.cache_creation_input_tokens),
		...(webSearchRequests != null && {
			webSearchRequests: tokenCount(webSearchRequests),
		}),
	};
};

/**
 * Prompt-inclusive usage (an agent usage event, or a stored message's
 * columns) in the normalized shape: the cache reads and cache writes are
 * taken out of the prompt count, leaving the uncached input. Each is clamped
 * to what is left of the prompt, so an inconsistent report can never produce
 * a negative input count.
 */
export const normalizePromptInclusiveUsage = (
	usage: LlmPromptInclusiveUsageModel | null | undefined,
): LlmUsageForPricingModel | null => {
	if (usage == null) {
		return null;
	}

	const promptTokens = tokenCount(usage.promptTokens);
	const cacheRead = Math.min(tokenCount(usage.cachedTokens), promptTokens);
	const cacheWrite = Math.min(
		tokenCount(usage.cacheCreationTokens),
		promptTokens - cacheRead,
	);

	return {
		inputTokens: promptTokens - cacheRead - cacheWrite,
		outputTokens: tokenCount(usage.completionTokens),
		cacheReadInputTokens: cacheRead,
		cacheCreationInputTokens: cacheWrite,
	};
};

/** The rate card a request is billed on, chosen by the length of its whole prompt. */
const ratesForPrompt = (
	cost: LlmModelCostModel,
	promptTokens: number,
): LlmTokenRatesModel =>
	cost.longPrompt != null && promptTokens > cost.longPrompt.promptTokensAbove
		? cost.longPrompt
		: cost;

/**
 * Convert one call's usage to dollars at the provider's list price for the
 * model, or null when the model has no cost entry. Callers must surface the
 * null (an "unpriceable" warning), never treat it as zero: zero-cost is
 * exactly the wrong failure mode for a budgeting system.
 *
 * Uncached input, cache reads, cache writes and output are each priced at
 * their own rate, on the long-prompt card when the model has one and the
 * prompt is past its threshold. Hosted web searches add a per-request
 * surcharge. Not rounded: round with `roundLlmCostUsd` where it is stored.
 */
export const usageToUsd = (
	usage: LlmUsageForPricingModel,
	modelId: string | null | undefined,
): number | null => {
	const cost = findLlmModelCost(modelId);

	if (cost == null) {
		return null;
	}

	const rates = ratesForPrompt(cost, llmPromptTokens(usage));
	const perTok = (tokens: number | null | undefined, usdPerMTok: number) =>
		(tokenCount(tokens) * usdPerMTok) / 1_000_000;

	return (
		perTok(usage.inputTokens, rates.inputUsdPerMTok) +
		perTok(usage.outputTokens, rates.outputUsdPerMTok) +
		perTok(usage.cacheReadInputTokens, rates.cacheReadUsdPerMTok) +
		perTok(usage.cacheCreationInputTokens, rates.cacheWriteUsdPerMTok) +
		tokenCount(usage.webSearchRequests) * WEB_SEARCH_USD_PER_REQUEST
	);
};

/**
 * A per-call cost at the precision it is stored with: `llm_cost` is
 * DECIMAL(12,6), and rounding here keeps the stored figure equal to the one
 * a test or a log computes.
 */
export const roundLlmCostUsd = (usd: number): number =>
	Math.round(usd * 1_000_000) / 1_000_000;

/**
 * The cost recorded for one agent turn (`_conversation_message.llm_cost`):
 * the turn's prompt-inclusive usage, normalized, priced by `usageToUsd` and
 * rounded to the column. Null when the turn reported no usage or the model
 * has no cost entry; never a guess.
 */
export const llmTurnCostUsd = (
	modelId: string | null | undefined,
	usage: LlmPromptInclusiveUsageModel | null | undefined,
): number | null => {
	const normalized = normalizePromptInclusiveUsage(usage);

	if (normalized == null) {
		return null;
	}

	const usd = usageToUsd(normalized, modelId);

	return usd == null ? null : roundLlmCostUsd(usd);
};

/** Render a dollar amount for UI/step output: cents precision, floor at 1c shown as "<$0.01" when non-zero. */
export const formatUsdEstimate = (usd: number): string => {
	if (usd > 0 && usd < 0.01) {
		return '<$0.01';
	}

	return `$${usd.toFixed(2)}`;
};
